import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, Workflow } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as ss from 'simple-statistics';
// @ts-ignore
import jStat from 'jstat';
import { ParsedWorksheet, WorksheetColumn } from './worksheet';

interface NonparametricsAnalysisProps {
  worksheet: ParsedWorksheet;
  selectedColumnId?: string;
}

type TestType = 'mann-whitney' | 'wilcoxon-signed-rank' | 'kruskal-wallis' | 'sign-test' | 'runs-test';

type RankedValue = {
  value: number;
  rank: number;
};

const formatNumber = (value: number, digits = 4) => (Number.isFinite(value) ? value.toFixed(digits) : '—');

const describeEffectSize = (value: number) => {
  const magnitude = Math.abs(value);
  if (!Number.isFinite(magnitude)) return '—';
  if (magnitude < 0.1) return 'Negligible';
  if (magnitude < 0.3) return 'Small';
  if (magnitude < 0.5) return 'Moderate';
  return 'Large';
};

const normalTwoSidedP = (z: number) => {
  if (!Number.isFinite(z)) return Number.NaN;
  return 2 * (1 - jStat.normal.cdf(Math.abs(z), 0, 1));
};

const binomialCoefficient = (n: number, k: number) => {
  if (k < 0 || k > n) return 0;
  const m = Math.min(k, n - k);
  let result = 1;
  for (let index = 1; index <= m; index += 1) {
    result = (result * (n - m + index)) / index;
  }
  return result;
};

const exactSignTestP = (positive: number, negative: number) => {
  const n = positive + negative;
  if (n <= 0) return Number.NaN;
  const tail = Math.min(positive, negative);
  let cumulative = 0;
  for (let k = 0; k <= tail; k += 1) {
    cumulative += binomialCoefficient(n, k);
  }
  return Math.min(1, (2 * cumulative) / 2 ** n);
};

const rankValues = (values: number[]) => {
  const sorted = values
    .map((value, index) => ({ value, index }))
    .sort((a, b) => a.value - b.value);

  const ranks = Array.from({ length: values.length }, () => 0);
  const tieGroups: number[] = [];
  let position = 0;

  while (position < sorted.length) {
    let end = position + 1;
    while (end < sorted.length && sorted[end].value === sorted[position].value) {
      end += 1;
    }

    const averageRank = ((position + 1) + end) / 2;
    for (let index = position; index < end; index += 1) {
      ranks[sorted[index].index] = averageRank;
    }
    tieGroups.push(end - position);
    position = end;
  }

  return { ranks, tieGroups };
};

const tieCorrection = (groupSizes: number[], totalCount: number) => {
  if (totalCount <= 1) return 1;
  const numerator = groupSizes.reduce((sum, size) => sum + (size ** 3 - size), 0);
  const denominator = totalCount ** 3 - totalCount;
  return denominator === 0 ? 1 : 1 - numerator / denominator;
};

const holmAdjust = (pValues: number[]) => {
  if (pValues.length === 0) return [];

  const ranked = pValues
    .map((pValue, index) => ({ pValue, index }))
    .sort((a, b) => a.pValue - b.pValue);

  const adjusted = Array.from({ length: pValues.length }, () => 1);
  let runningMaximum = 0;

  ranked.forEach((entry, sortedIndex) => {
    const scaled = Math.min(1, entry.pValue * (pValues.length - sortedIndex));
    runningMaximum = Math.max(runningMaximum, scaled);
    adjusted[entry.index] = runningMaximum;
  });

  return adjusted;
};

const mannWhitney = (columnA?: WorksheetColumn, columnB?: WorksheetColumn) => {
  const sampleA = columnA?.numericValues ?? [];
  const sampleB = columnB?.numericValues ?? [];
  if (sampleA.length < 2 || sampleB.length < 2) return null;

  const pooled = [...sampleA, ...sampleB];
  const { ranks, tieGroups } = rankValues(pooled);
  const n1 = sampleA.length;
  const n2 = sampleB.length;
  const rankSumA = ranks.slice(0, n1).reduce((sum, value) => sum + value, 0);
  const rankSumB = ranks.slice(n1).reduce((sum, value) => sum + value, 0);
  const u1 = rankSumA - (n1 * (n1 + 1)) / 2;
  const u2 = rankSumB - (n2 * (n2 + 1)) / 2;
  const u = Math.min(u1, u2);
  const correction = tieCorrection(tieGroups, pooled.length);
  const variance = correction * ((n1 * n2 * (n1 + n2 + 1)) / 12);
  const z = variance > 0 ? (u - (n1 * n2) / 2) / Math.sqrt(variance) : Number.NaN;

  return {
    n1,
    n2,
    u,
    u1,
    u2,
    z,
    pValue: normalTwoSidedP(z),
    medians: [ss.median(sampleA), ss.median(sampleB)],
    means: [ss.mean(sampleA), ss.mean(sampleB)],
    chartData: [
      { group: columnA?.name || 'Sample A', median: ss.median(sampleA), mean: ss.mean(sampleA), count: n1 },
      { group: columnB?.name || 'Sample B', median: ss.median(sampleB), mean: ss.mean(sampleB), count: n2 },
    ],
  };
};

const wilcoxonSignedRank = (columnA?: WorksheetColumn, columnB?: WorksheetColumn) => {
  if (!columnA || !columnB) return null;

  const pairs = columnA.values.flatMap((valueA, rowIndex) => {
    const a = Number(valueA ?? '');
    const b = Number(columnB.values[rowIndex] ?? '');
    if (!Number.isFinite(a) || !Number.isFinite(b)) return [];
    const difference = a - b;
    if (difference === 0) return [];
    return [{ rowIndex: rowIndex + 1, a, b, difference, absDifference: Math.abs(difference), sign: difference > 0 ? 'positive' : 'negative' }];
  });

  if (pairs.length < 3) return null;

  const { ranks } = rankValues(pairs.map((pair) => pair.absDifference));
  const rankedPairs = pairs.map((pair, index) => ({ ...pair, rank: ranks[index] }));
  const positiveRankSum = rankedPairs.filter((pair) => pair.difference > 0).reduce((sum, pair) => sum + pair.rank, 0);
  const negativeRankSum = rankedPairs.filter((pair) => pair.difference < 0).reduce((sum, pair) => sum + pair.rank, 0);
  const testStatistic = Math.min(positiveRankSum, negativeRankSum);
  const n = rankedPairs.length;
  const variance = (n * (n + 1) * (2 * n + 1)) / 24;
  const expected = (n * (n + 1)) / 4;
  const z = variance > 0 ? (testStatistic - expected) / Math.sqrt(variance) : Number.NaN;

  return {
    n,
    positiveRankSum,
    negativeRankSum,
    testStatistic,
    z,
    pValue: normalTwoSidedP(z),
    medianDifference: ss.median(rankedPairs.map((pair) => pair.difference)),
    meanDifference: ss.mean(rankedPairs.map((pair) => pair.difference)),
    preview: rankedPairs.slice(0, 12),
  };
};

const kruskalWallis = (responseColumn?: WorksheetColumn, groupColumn?: WorksheetColumn) => {
  if (!responseColumn || !groupColumn) return null;

  const rows = responseColumn.values.flatMap((value, rowIndex) => {
    const response = Number(value ?? '');
    const group = String(groupColumn.values[rowIndex] ?? '').trim();
    if (!Number.isFinite(response) || !group) return [];
    return [{ response, group, rowIndex: rowIndex + 1 }];
  });

  const groups = Array.from(new Set(rows.map((row) => row.group)));
  if (groups.length < 2 || rows.length < 3) return null;

  const { ranks, tieGroups } = rankValues(rows.map((row) => row.response));
  const rankedRows = rows.map((row, index) => ({ ...row, rank: ranks[index] }));
  const summaries = groups.map((group) => {
    const values = rankedRows.filter((row) => row.group === group);
    return {
      group,
      count: values.length,
      rankSum: values.reduce((sum, row) => sum + row.rank, 0),
      median: ss.median(values.map((row) => row.response)),
      meanRank: ss.mean(values.map((row) => row.rank)),
    };
  });

  const n = rankedRows.length;
  const hBase = (12 / (n * (n + 1))) * summaries.reduce((sum, summary) => sum + (summary.rankSum ** 2) / summary.count, 0) - 3 * (n + 1);
  const correction = tieCorrection(tieGroups, n);
  const h = correction > 0 ? hBase / correction : hBase;
  const degreesOfFreedom = summaries.length - 1;
  const epsilonSquared = n > summaries.length ? Math.max(0, (h - summaries.length + 1) / (n - summaries.length)) : Number.NaN;
  const basePairwiseVariance = correction * (n * (n + 1) / 12);
  const confidenceLevel = 0.95;
  const criticalValue = jStat.normal.inv(1 - ((1 - confidenceLevel) / 2), 0, 1);

  const rawComparisons = summaries.flatMap((leftSummary, leftIndex) => summaries.slice(leftIndex + 1).map((rightSummary) => {
    const meanRankDifference = leftSummary.meanRank - rightSummary.meanRank;
    const standardError = basePairwiseVariance > 0
      ? Math.sqrt(basePairwiseVariance * ((1 / leftSummary.count) + (1 / rightSummary.count)))
      : Number.NaN;
    const z = standardError > 0 ? meanRankDifference / standardError : Number.NaN;
    const pValue = normalTwoSidedP(z);
    const effectSizeR = Number.isFinite(z) && n > 0 ? Math.abs(z) / Math.sqrt(n) : Number.NaN;
    const confidenceIntervalLower = Number.isFinite(standardError) ? meanRankDifference - criticalValue * standardError : Number.NaN;
    const confidenceIntervalUpper = Number.isFinite(standardError) ? meanRankDifference + criticalValue * standardError : Number.NaN;

    return {
      comparison: `${leftSummary.group} vs ${rightSummary.group}`,
      leftGroup: leftSummary.group,
      rightGroup: rightSummary.group,
      countLeft: leftSummary.count,
      countRight: rightSummary.count,
      meanRankLeft: leftSummary.meanRank,
      meanRankRight: rightSummary.meanRank,
      meanRankDifference,
      standardError,
      confidenceIntervalLower,
      confidenceIntervalUpper,
      z,
      pValue,
      effectSizeR,
      effectSizeLabel: describeEffectSize(effectSizeR),
    };
  }));

  const adjustedPValues = holmAdjust(rawComparisons.map((comparison) => comparison.pValue));
  const pairwiseComparisons = rawComparisons.map((comparison, index) => ({
    ...comparison,
    adjustedPValue: adjustedPValues[index],
    significant: adjustedPValues[index] < 0.05,
  }));

  return {
    n,
    groups: summaries.length,
    h,
    degreesOfFreedom,
    epsilonSquared,
    confidenceLevel,
    pValue: 1 - jStat.chisquare.cdf(h, degreesOfFreedom),
    summaries,
    pairwiseComparisons,
  };
};

const signTest = (column?: WorksheetColumn, nullMedian = 0) => {
  const values = column?.numericValues ?? [];
  if (values.length < 3) return null;

  const positive = values.filter((value) => value > nullMedian).length;
  const negative = values.filter((value) => value < nullMedian).length;
  const ties = values.length - positive - negative;
  const effectiveN = positive + negative;
  if (effectiveN === 0) return null;

  return {
    n: effectiveN,
    positive,
    negative,
    ties,
    pValue: exactSignTestP(positive, negative),
    observedMedian: ss.median(values),
    chartData: [
      { side: 'Above null', count: positive },
      { side: 'Below null', count: negative },
      { side: 'Equal to null', count: ties },
    ],
  };
};

const runsTest = (column?: WorksheetColumn, center = 0) => {
  const values = column?.numericValues ?? [];
  if (values.length < 4) return null;

  const labeled = values
    .map((value, index) => ({
      order: index + 1,
      value,
      side: value > center ? 'Above' : value < center ? 'Below' : 'Equal',
      center,
    }))
    .filter((point) => point.side !== 'Equal');

  if (labeled.length < 4) return null;

  const nAbove = labeled.filter((point) => point.side === 'Above').length;
  const nBelow = labeled.filter((point) => point.side === 'Below').length;
  if (nAbove === 0 || nBelow === 0) return null;

  let runs = 1;
  for (let index = 1; index < labeled.length; index += 1) {
    if (labeled[index].side !== labeled[index - 1].side) {
      runs += 1;
    }
  }

  const total = nAbove + nBelow;
  const meanRuns = (2 * nAbove * nBelow) / total + 1;
  const variance = (2 * nAbove * nBelow * (2 * nAbove * nBelow - nAbove - nBelow)) / (total ** 2 * (total - 1));
  const z = variance > 0 ? (runs - meanRuns) / Math.sqrt(variance) : Number.NaN;

  return {
    nAbove,
    nBelow,
    runs,
    meanRuns,
    z,
    pValue: normalTwoSidedP(z),
    preview: labeled.slice(0, 20),
  };
};

export const NonparametricsAnalysis: React.FC<NonparametricsAnalysisProps> = ({ worksheet, selectedColumnId }) => {
  const numericColumns = worksheet.numericColumns.filter((column) => column.numericCount >= 3);
  const groupColumns = worksheet.columns.filter((column) => column.nonEmptyCount >= 3);

  const [testType, setTestType] = useState<TestType>('mann-whitney');
  const [primaryColumnId, setPrimaryColumnId] = useState(selectedColumnId ?? numericColumns[0]?.id ?? '');
  const [secondaryColumnId, setSecondaryColumnId] = useState(numericColumns[1]?.id ?? numericColumns[0]?.id ?? '');
  const [groupColumnId, setGroupColumnId] = useState(groupColumns.find((column) => column.id !== selectedColumnId)?.id ?? groupColumns[0]?.id ?? '');
  const [nullMedian, setNullMedian] = useState('0');
  const [runsCenter, setRunsCenter] = useState('');

  useEffect(() => {
    if (!primaryColumnId && numericColumns[0]) {
      setPrimaryColumnId(selectedColumnId && numericColumns.some((column) => column.id === selectedColumnId) ? selectedColumnId : numericColumns[0].id);
    }
    if (!secondaryColumnId && numericColumns[1]) {
      setSecondaryColumnId(numericColumns[1].id);
    }
    if (!groupColumnId && groupColumns[0]) {
      setGroupColumnId(groupColumns[0].id);
    }
  }, [groupColumns, groupColumnId, numericColumns, primaryColumnId, secondaryColumnId, selectedColumnId]);

  const primaryColumn = numericColumns.find((column) => column.id === primaryColumnId) ?? numericColumns[0];
  const secondaryColumn = numericColumns.find((column) => column.id === secondaryColumnId) ?? numericColumns[1] ?? numericColumns[0];
  const groupColumn = groupColumns.find((column) => column.id === groupColumnId) ?? groupColumns[0];

  const signNull = Number.isFinite(Number(nullMedian)) ? Number(nullMedian) : 0;
  const runCenter = runsCenter.trim() ? Number(runsCenter) : (primaryColumn?.numericValues.length ? ss.median(primaryColumn.numericValues) : 0);

  const mannWhitneyResult = useMemo(() => mannWhitney(primaryColumn, secondaryColumn), [primaryColumn, secondaryColumn]);
  const wilcoxonResult = useMemo(() => wilcoxonSignedRank(primaryColumn, secondaryColumn), [primaryColumn, secondaryColumn]);
  const kruskalResult = useMemo(() => kruskalWallis(primaryColumn, groupColumn), [groupColumn, primaryColumn]);
  const signResult = useMemo(() => signTest(primaryColumn, signNull), [primaryColumn, signNull]);
  const runsResult = useMemo(() => runsTest(primaryColumn, runCenter), [primaryColumn, runCenter]);

  if (numericColumns.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No numeric data available</AlertTitle>
        <AlertDescription>Paste numeric worksheet data to unlock the nonparametric procedures.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Badge>Ready</Badge>
                <Badge variant="outline">Distribution-free methods</Badge>
              </div>
              <CardTitle className="mt-3 flex items-center gap-2 text-3xl">
                <Workflow className="h-8 w-8 text-neutral-700 dark:text-neutral-200" />
                Nonparametrics
              </CardTitle>
              <CardDescription className="mt-2 max-w-3xl text-base">
                Run rank-based and distribution-free workflows for medians, paired comparisons, grouped data, and sequence randomness.
              </CardDescription>
            </div>
            <div className="grid min-w-[15rem] grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl border bg-muted/30 p-3">
                <div className="text-muted-foreground">Numeric columns</div>
                <div className="mt-1 text-2xl font-semibold">{numericColumns.length}</div>
              </div>
              <div className="rounded-xl border bg-muted/30 p-3">
                <div className="text-muted-foreground">Worksheet rows</div>
                <div className="mt-1 text-2xl font-semibold">{worksheet.rowCount}</div>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Procedure setup</CardTitle>
          <CardDescription>Choose a test and map the needed worksheet columns.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-2">
            <Label>Procedure</Label>
            <Select value={testType} onValueChange={(value: TestType) => setTestType(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mann-whitney">Mann-Whitney U</SelectItem>
                <SelectItem value="wilcoxon-signed-rank">Wilcoxon signed-rank</SelectItem>
                <SelectItem value="kruskal-wallis">Kruskal-Wallis</SelectItem>
                <SelectItem value="sign-test">Sign test</SelectItem>
                <SelectItem value="runs-test">Runs test</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{testType === 'kruskal-wallis' ? 'Response column' : 'Primary numeric column'}</Label>
            <Select value={primaryColumnId} onValueChange={setPrimaryColumnId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {numericColumns.map((column) => (
                  <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(testType === 'mann-whitney' || testType === 'wilcoxon-signed-rank') && (
            <div className="space-y-2">
              <Label>Comparison column</Label>
              <Select value={secondaryColumnId} onValueChange={setSecondaryColumnId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {numericColumns.map((column) => (
                    <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {testType === 'kruskal-wallis' && (
            <div className="space-y-2">
              <Label>Group column</Label>
              <Select value={groupColumnId} onValueChange={setGroupColumnId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {groupColumns.map((column) => (
                    <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {testType === 'sign-test' && (
            <div className="space-y-2">
              <Label>Null median</Label>
              <Input value={nullMedian} onChange={(event) => setNullMedian(event.target.value)} />
            </div>
          )}

          {testType === 'runs-test' && (
            <div className="space-y-2">
              <Label>Center line</Label>
              <Input value={runsCenter} onChange={(event) => setRunsCenter(event.target.value)} placeholder="Blank = worksheet median" />
            </div>
          )}
        </CardContent>
      </Card>

      {testType === 'mann-whitney' && (
        mannWhitneyResult ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">U statistic</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(mannWhitneyResult.u)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Z</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(mannWhitneyResult.z)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">p-value</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(mannWhitneyResult.pValue)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Median A</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(mannWhitneyResult.medians[0])}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Median B</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(mannWhitneyResult.medians[1])}</div></CardContent></Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Sample comparison</CardTitle>
                <CardDescription>Mean and median profile for the two independent samples.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={mannWhitneyResult.chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="group" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="median" fill="#2563eb" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="mean" fill="#16a34a" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <Alert><AlertCircle className="h-4 w-4" /><AlertTitle>Need two usable numeric samples</AlertTitle><AlertDescription>Select two numeric columns with at least two values each for Mann-Whitney.</AlertDescription></Alert>
        )
      )}

      {testType === 'wilcoxon-signed-rank' && (
        wilcoxonResult ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Pairs used</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{wilcoxonResult.n}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">W statistic</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(wilcoxonResult.testStatistic)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Z</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(wilcoxonResult.z)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">p-value</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(wilcoxonResult.pValue)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Median diff</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(wilcoxonResult.medianDifference)}</div></CardContent></Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Paired difference preview</CardTitle>
                <CardDescription>First paired rows contributing to the signed-rank statistic.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead className="text-right">A</TableHead>
                      <TableHead className="text-right">B</TableHead>
                      <TableHead className="text-right">Diff</TableHead>
                      <TableHead className="text-right">Rank</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wilcoxonResult.preview.map((pair) => (
                      <TableRow key={pair.rowIndex}>
                        <TableCell>{pair.rowIndex}</TableCell>
                        <TableCell className="text-right">{formatNumber(pair.a)}</TableCell>
                        <TableCell className="text-right">{formatNumber(pair.b)}</TableCell>
                        <TableCell className="text-right">{formatNumber(pair.difference)}</TableCell>
                        <TableCell className="text-right">{formatNumber(pair.rank)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          <Alert><AlertCircle className="h-4 w-4" /><AlertTitle>Need paired data</AlertTitle><AlertDescription>Select two numeric columns with at least three usable paired differences.</AlertDescription></Alert>
        )
      )}

      {testType === 'kruskal-wallis' && (
        kruskalResult ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Groups</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{kruskalResult.groups}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">H statistic</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(kruskalResult.h)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">df</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{kruskalResult.degreesOfFreedom}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Overall p-value</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(kruskalResult.pValue)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Epsilon-squared</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(kruskalResult.epsilonSquared)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Pairwise tests</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{kruskalResult.pairwiseComparisons.length}</div></CardContent></Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Group summary</CardTitle>
                <CardDescription>Median response and mean rank by group.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={kruskalResult.summaries}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="group" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="median" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="meanRank" fill="#f97316" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Group</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                      <TableHead className="text-right">Median</TableHead>
                      <TableHead className="text-right">Mean rank</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kruskalResult.summaries.map((summary) => (
                      <TableRow key={summary.group}>
                        <TableCell>{summary.group}</TableCell>
                        <TableCell className="text-right">{summary.count}</TableCell>
                        <TableCell className="text-right">{formatNumber(summary.median)}</TableCell>
                        <TableCell className="text-right">{formatNumber(summary.meanRank)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Post-hoc pairwise comparisons</CardTitle>
                <CardDescription>Dunn-style rank comparisons with Holm-adjusted p-values after the omnibus Kruskal-Wallis test.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                  Confidence intervals are shown for the mean-rank difference at {Math.round(kruskalResult.confidenceLevel * 100)}% confidence.
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={kruskalResult.pairwiseComparisons}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="comparison" interval={0} angle={-18} textAnchor="end" height={72} />
                      <YAxis domain={[0, 1]} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="pValue" name="Raw p-value" fill="#94a3b8" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="adjustedPValue" name="Holm-adjusted p-value" fill="#2563eb" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Comparison</TableHead>
                      <TableHead className="text-right">Mean rank diff</TableHead>
                      <TableHead className="text-right">95% CI</TableHead>
                      <TableHead className="text-right">Z</TableHead>
                      <TableHead className="text-right">Effect size (r)</TableHead>
                      <TableHead className="text-right">Raw p</TableHead>
                      <TableHead className="text-right">Adjusted p</TableHead>
                      <TableHead className="text-right">Decision</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kruskalResult.pairwiseComparisons.map((comparison) => (
                      <TableRow key={comparison.comparison}>
                        <TableCell>
                          <div className="font-medium">{comparison.comparison}</div>
                          <div className="text-xs text-muted-foreground">
                            n = {comparison.countLeft} vs {comparison.countRight}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(comparison.meanRankDifference)}</TableCell>
                        <TableCell className="text-right">
                          <div>[{formatNumber(comparison.confidenceIntervalLower)}, {formatNumber(comparison.confidenceIntervalUpper)}]</div>
                          <div className="text-xs text-muted-foreground">SE {formatNumber(comparison.standardError)}</div>
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(comparison.z)}</TableCell>
                        <TableCell className="text-right">
                          <div>{formatNumber(comparison.effectSizeR)}</div>
                          <div className="text-xs text-muted-foreground">{comparison.effectSizeLabel}</div>
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(comparison.pValue)}</TableCell>
                        <TableCell className="text-right">{formatNumber(comparison.adjustedPValue)}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={comparison.significant ? 'default' : 'secondary'}>
                            {comparison.significant ? 'Different' : 'Not different'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          <Alert><AlertCircle className="h-4 w-4" /><AlertTitle>Need grouped response data</AlertTitle><AlertDescription>Select a numeric response and a grouping column with at least two populated groups.</AlertDescription></Alert>
        )
      )}

      {testType === 'sign-test' && (
        signResult ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Effective n</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{signResult.n}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Above null</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{signResult.positive}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Below null</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{signResult.negative}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Ties</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{signResult.ties}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">p-value</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(signResult.pValue)}</div></CardContent></Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Sign breakdown</CardTitle>
                <CardDescription>Counts relative to the hypothesized median.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={signResult.chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="side" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <Alert><AlertCircle className="h-4 w-4" /><AlertTitle>Need more usable observations</AlertTitle><AlertDescription>Select a numeric column with at least three values not tied to the null median.</AlertDescription></Alert>
        )
      )}

      {testType === 'runs-test' && (
        runsResult ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Runs</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{runsResult.runs}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Expected runs</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(runsResult.meanRuns)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Z</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(runsResult.z)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">p-value</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(runsResult.pValue)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Center line</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(runCenter)}</div></CardContent></Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Sequence view</CardTitle>
                <CardDescription>Observation order relative to the chosen center line.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={runsResult.preview}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="order" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2} dot />
                      <Line type="monotone" dataKey="center" stroke="#dc2626" strokeDasharray="6 4" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <Alert><AlertCircle className="h-4 w-4" /><AlertTitle>Runs test needs variation on both sides</AlertTitle><AlertDescription>Select a numeric column with values above and below the chosen center line.</AlertDescription></Alert>
        )
      )}
    </div>
  );
};